import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useMemo, useState } from 'react';
import {
  Activity,
  Building2,
  MessagesSquare,
  Radio,
  ShieldAlert,
  Users2,
} from 'lucide-react';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Select } from '@/shared/components/ui/Select';
import { WidgetLoading } from '@/features/dashboard/infrastructure/widgets/WidgetStates';
import {
  useAllActivity,
  useCommLog,
  useOversightConversations,
  useOversightMessages,
  usePresence,
  type OversightConversation,
} from '../api';

function relativeTime(value: string | null): string {
  if (!value) return 'never';
  const diff = Date.now() - new Date(value).getTime();
  if (Number.isNaN(diff)) return '—';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(value).toLocaleDateString('en-IN', { dateStyle: 'medium' });
}

function dateTime(value?: string): string {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

type Tab = 'live' | 'activity' | 'chats' | 'comms';

/**
 * Super Admin Live Oversight (spec §3). Company-owned visibility for the account
 * owner: who is online and in which section, the full activity feed, and read
 * access to every internal conversation and the company WhatsApp/email log.
 * Reading private chat content is itself written to the audit log.
 */
export function OversightPage() {
  const [tab, setTab] = useState<Tab>('live');

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-4 md:p-6">
      <PageHeader
        icon={ShieldAlert}
        eyebrow="Insights"
        title="Live Oversight"
        description={<>Owner view of who is active, what is happening, and every internal conversation</>}
      />

      <div role="tablist" aria-label="Oversight sections" className="flex flex-wrap gap-1 border-b border-outline">
        {(
          [
            { key: 'live', label: 'Live Presence', icon: Radio },
            { key: 'activity', label: 'Activity Feed', icon: Activity },
            { key: 'chats', label: 'Internal Chats', icon: MessagesSquare },
            { key: 'comms', label: 'WhatsApp / Email', icon: Building2 },
          ] as const
        ).map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 rounded-t-md px-4 py-2 text-body-sm font-medium transition-colors ${
              tab === t.key ? 'border-b-2 border-primary text-primary' : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <t.icon className="h-4 w-4" aria-hidden />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'live' && <LiveTab />}
      {tab === 'activity' && <ActivityTab />}
      {tab === 'chats' && <ChatsTab />}
      {tab === 'comms' && <CommsTab />}
    </div>
  );
}

/* ── Live presence ───────────────────────────────────────────────────────── */

function LiveTab() {
  const { data, isLoading } = usePresence();

  if (isLoading || !data) {
    return (
      <div className="p-4"><WidgetLoading variant="table" /></div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Online now" value={data.onlineCount} tone="text-success-text" />
        <StatTile label="Total staff" value={data.totalUsers} />
        <StatTile label="Active sections" value={data.bySection.length} />
        <StatTile label="Offline" value={data.totalUsers - data.onlineCount} tone="text-on-surface-variant" />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader title="Who's where right now" subtitle="Active users grouped by the section they're viewing" />
          <CardBody>
            {data.bySection.length === 0 ? (
              <p className="py-6 text-center text-body-sm text-on-surface-variant">No one is active right now.</p>
            ) : (
              <ul className="flex flex-col gap-3">
                {data.bySection.map((group) => (
                  <li key={group.section}>
                    <div className="mb-1 flex items-center gap-2">
                      <Users2 className="h-4 w-4 text-primary" aria-hidden />
                      <span className="font-medium text-on-surface">{group.section}</span>
                      <Badge tone="success">{group.members.length}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-1.5 pl-6">
                      {group.members.map((m) => (
                        <span key={m.userId} className="rounded-full bg-surface-variant px-2 py-0.5 text-body-sm text-on-surface">
                          {m.name}
                          <span className="text-on-surface-variant"> · {m.departmentLabel}</span>
                        </span>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="All employees" subtitle="Live status and last-seen" />
          <CardBody className="p-0">
            <div className="max-h-[28rem] overflow-y-auto">
              <table className="w-full text-left text-body-sm">
                <thead className="sticky top-0 bg-surface-variant text-on-surface-variant">
                  <tr>
                    <th scope="col" className="px-3 py-2 font-medium">Employee</th>
                    <th scope="col" className="px-3 py-2 font-medium">Department</th>
                    <th scope="col" className="px-3 py-2 font-medium">Status</th>
                    <th scope="col" className="px-3 py-2 font-medium">Section / last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((user) => (
                    <tr key={user.userId} className="border-t border-outline">
                      <td className="px-3 py-2 font-medium text-on-surface">{user.name}</td>
                      <td className="px-3 py-2 text-on-surface-variant">{user.departmentLabel}</td>
                      <td className="px-3 py-2">
                        <span className="flex items-center gap-1.5">
                          <span className={`h-2 w-2 rounded-full ${user.online ? 'bg-success' : 'bg-outline'}`} aria-hidden />
                          {user.online ? 'Online' : 'Offline'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-on-surface-variant">
                        {user.online ? user.section || 'Idle' : relativeTime(user.lastSeenAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function StatTile({ label, value, tone = 'text-on-surface' }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-lg border border-outline bg-surface p-4">
      <p className={`text-display-sm font-bold ${tone}`}>{value}</p>
      <p className="text-body-sm text-on-surface-variant">{label}</p>
    </div>
  );
}

/* ── Activity feed ───────────────────────────────────────────────────────── */

function ActivityTab() {
  const { data: presence } = usePresence();
  const [userId, setUserId] = useState('');
  const { data: activity = [], isLoading } = useAllActivity(userId);

  const userOptions = useMemo(
    () => (presence?.users ?? []).map((u) => ({ label: `${u.name} (${u.departmentLabel})`, value: u.userId })),
    [presence],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="w-72">
        <Select
          label="Filter by employee"
          name="activity-user"
          options={userOptions}
          placeholder="Everyone"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
        />
      </div>
      {isLoading ? (
        <div className="p-4"><WidgetLoading variant="table" /></div>
      ) : activity.length === 0 ? (
        <EmptyState title="No activity yet" description="Employee actions across every module will appear here." />
      ) : (
        <ol className="flex flex-col gap-2">
          {activity.map((entry) => (
            <li key={entry.id ?? entry._id} className="flex items-start gap-3 rounded-lg border border-outline bg-surface p-3">
              <span className="mt-0.5 rounded-full bg-primary/10 p-1.5 text-primary">
                <Activity className="h-3.5 w-3.5" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-body-sm text-on-surface">
                  <span className="font-medium">{entry.user_name || entry.display_name || 'Someone'}</span>{' '}
                  {entry.detail || entry.action}
                </p>
                <p className="text-body-sm text-on-surface-variant">
                  {entry.action} · {dateTime(entry.created_at)}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/* ── Internal chats ──────────────────────────────────────────────────────── */

function chatTypeTone(type: OversightConversation['type']): BadgeTone {
  if (type === 'company') return 'primary';
  if (type === 'department') return 'info';
  if (type === 'group') return 'neutral';
  return 'warning';
}

function ChatsTab() {
  const { data: conversations = [], isLoading } = useOversightConversations();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data: thread, isLoading: threadLoading } = useOversightMessages(selectedId);

  return (
    <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 overflow-hidden rounded-lg border border-outline md:grid-cols-[340px_1fr]">
      <div className="flex min-h-0 flex-col border-r border-outline bg-surface">
        <div className="border-b border-outline px-3 py-2 text-body-sm font-medium text-on-surface">
          All conversations
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-4"><WidgetLoading variant="list" /></div>
          ) : conversations.length === 0 ? (
            <p className="p-4 text-body-sm text-on-surface-variant">No conversations yet.</p>
          ) : (
            conversations.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                className={`flex w-full flex-col gap-0.5 border-b border-outline/60 px-3 py-2.5 text-left transition-colors ${
                  selectedId === c.id ? 'bg-primary/5' : 'hover:bg-surface-variant/60'
                }`}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium text-on-surface">{c.label}</span>
                  <Badge tone={chatTypeTone(c.type)}>{c.type === 'dm' ? 'DM' : c.type}</Badge>
                </span>
                <span className="truncate text-body-sm text-on-surface-variant">
                  {c.lastMessagePreview
                    ? `${c.lastMessageByName ? `${c.lastMessageByName}: ` : ''}${c.lastMessagePreview}`
                    : 'No messages yet'}
                </span>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="flex min-h-0 flex-col bg-surface-variant/30">
        {!selectedId ? (
          <div className="flex flex-1 items-center justify-center p-6">
            <EmptyState
              icon={<MessagesSquare className="h-8 w-8" aria-hidden />}
              title="Select a conversation"
              description="Open any DM, group, department or company room to read its full history."
            />
          </div>
        ) : threadLoading || !thread ? (
          <div className="p-4"><WidgetLoading variant="list" /></div>
        ) : (
          <>
            <div className="border-b border-outline bg-surface px-4 py-2.5">
              <p className="font-medium text-on-surface">{thread.conversation.name || thread.conversation.type}</p>
              <p className="text-body-sm text-on-surface-variant">Read-only · this view is recorded in the audit log</p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {thread.messages.length === 0 ? (
                <p className="text-center text-body-sm text-on-surface-variant">No messages in this conversation.</p>
              ) : (
                <ol className="flex flex-col gap-2">
                  {thread.messages.map((m) => (
                    <li key={m.id} className="rounded-lg border border-outline bg-surface p-2.5">
                      <p className="flex items-center justify-between gap-2 text-body-sm">
                        <span className="font-medium text-on-surface">{m.senderName}</span>
                        <span className="text-on-surface-variant">{dateTime(m.createdAt)}</span>
                      </p>
                      <p className="mt-0.5 whitespace-pre-wrap break-words text-data-md text-on-surface">{m.content}</p>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── WhatsApp / email log ────────────────────────────────────────────────── */

function CommsTab() {
  const [channel, setChannel] = useState<'' | 'email' | 'whatsapp'>('');
  const { data: logs = [], isLoading } = useCommLog(channel);

  return (
    <div className="flex flex-col gap-3">
      <div className="w-52">
        <Select
          label="Channel"
          name="comm-channel"
          options={[
            { label: 'WhatsApp', value: 'whatsapp' },
            { label: 'Email', value: 'email' },
          ]}
          placeholder="All channels"
          value={channel}
          onChange={(e) => setChannel(e.target.value as '' | 'email' | 'whatsapp')}
        />
      </div>
      <p className="text-body-sm text-on-surface-variant">
        Company WhatsApp Business and email messages sent or received through ZYNX. Personal or off-platform
        messages are not accessible.
      </p>
      {isLoading ? (
        <div className="p-4"><WidgetLoading variant="table" /></div>
      ) : logs.length === 0 ? (
        <EmptyState title="No messages logged" description="Company WhatsApp and email traffic will appear here once configured." />
      ) : (
        <div className="flex flex-col gap-2">
          {logs.map((log) => (
            <div key={log.id} className="rounded-lg border border-outline bg-surface p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={log.channel === 'email' ? 'info' : 'success'}>{log.channel}</Badge>
                <Badge tone={log.status === 'sent' ? 'success' : log.status === 'received' ? 'info' : 'danger'}>
                  {log.status}
                </Badge>
                <span className="text-body-sm text-on-surface-variant">
                  {log.direction === 'out' ? `To ${log.to}` : `From ${log.from}`} · {dateTime(log.createdAt)}
                </span>
              </div>
              {log.subject && <p className="mt-1 font-medium text-on-surface">{log.subject}</p>}
              <p className="mt-1 whitespace-pre-wrap text-body-sm text-on-surface-variant">{log.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
